import React, { useState } from 'react';
import { smsReallocationAPI } from '../../tracker/api/smsReallocation.api';
import '../../tracker/components/SmsReallocationModal/SmsReallocationModal.css';

type ActionType = 'move' | 'unlink';

interface SmsConversationReallocationModalProps {
  phoneNumber: string;
  currentAffaireId: number | null;
  onClose: () => void;
  onSuccess: () => void;
}

export const SmsConversationReallocationModal: React.FC<SmsConversationReallocationModalProps> = ({
  phoneNumber,
  currentAffaireId,
  onClose,
  onSuccess,
}) => {
  const [actionType, setActionType] = useState<ActionType>('move');
  const [targetAffaireId, setTargetAffaireId] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) onClose();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsLoading(true);

    try {
      if (actionType === 'move') {
        const targetId = parseInt(targetAffaireId, 10);
        if (isNaN(targetId) || targetId < 1000) {
          setError('Numéro d\'affaire invalide');
          setIsLoading(false);
          return;
        }
        await smsReallocationAPI.reassignByPhone(phoneNumber, targetId);
      } else {
        await smsReallocationAPI.unlinkByPhone(phoneNumber);
      }

      onSuccess();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Une erreur est survenue');
    } finally {
      setIsLoading(false);
    }
  };

  const needsTargetAffaire = actionType === 'move';

  return (
    <div className="sms-reallocation-modal-backdrop" onClick={handleBackdropClick}>
      <div className="sms-reallocation-modal">
        <div className="sms-reallocation-modal-header">
          <h3>Attribuer la conversation à un dossier</h3>
          <button
            className="sms-reallocation-modal-close"
            onClick={onClose}
            type="button"
          >
            &times;
          </button>
        </div>

        <form onSubmit={handleSubmit} className="sms-reallocation-modal-content">
          {/* Action Type Toggle */}
          <div className="sms-reallocation-action-grid">
            <button
              type="button"
              className={`sms-reallocation-action-btn ${actionType === 'move' ? 'active' : ''}`}
              onClick={() => { setActionType('move'); setError(null); }}
            >
              <span className="action-icon">&#x2194;&#xFE0F;</span>
              <span className="action-label">Attribuer</span>
            </button>
            <button
              type="button"
              className={`sms-reallocation-action-btn ${actionType === 'unlink' ? 'active' : ''}`}
              onClick={() => { setActionType('unlink'); setError(null); }}
            >
              <span className="action-icon">&#x1F517;</span>
              <span className="action-label">Dissocier</span>
            </button>
          </div>

          <div className="sms-reallocation-info">
            <div className="sms-reallocation-field">
              <label>Conversation:</label>
              <span className="current-affaire">{phoneNumber}</span>
            </div>
            {currentAffaireId && (
              <div className="sms-reallocation-field">
                <label>Actuellement lié à:</label>
                <span className="current-affaire">#{currentAffaireId}</span>
              </div>
            )}
          </div>

          {needsTargetAffaire && (
            <div className="sms-reallocation-input-group">
              <label htmlFor="smsConvTargetAffaireId">
                Numéro de dossier:
              </label>
              <input
                type="text"
                id="smsConvTargetAffaireId"
                value={targetAffaireId}
                onChange={(e) => {
                  const value = e.target.value.replace(/\D/g, '');
                  setTargetAffaireId(value);
                  setError(null);
                }}
                placeholder="Ex: 68011"
                pattern="\d{4,}"
                required
                autoFocus
              />
            </div>
          )}

          {/* Info message */}
          <div className={`sms-reallocation-info-message ${actionType}`}>
            <p>
              {actionType === 'move'
                ? 'Tous les SMS reçus de ce numéro seront attribués au dossier indiqué. Ils apparaîtront dans le tracker du dossier.'
                : 'Tous les SMS reçus de ce numéro seront dissociés de leur dossier actuel.'
              }
            </p>
          </div>

          {error && (
            <div className="sms-reallocation-error">
              {error}
            </div>
          )}

          <div className="sms-reallocation-modal-actions">
            <button
              type="button"
              className="btn-cancel"
              onClick={onClose}
              disabled={isLoading}
            >
              Annuler
            </button>
            <button
              type="submit"
              className={`btn-submit ${actionType}`}
              disabled={isLoading || (needsTargetAffaire && (!targetAffaireId || targetAffaireId.length < 4))}
            >
              {isLoading
                ? 'En cours...'
                : actionType === 'move'
                  ? 'Attribuer'
                  : 'Dissocier'
              }
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default SmsConversationReallocationModal;
